import React from 'react';
import { ActionItem, Pagination } from './types';
import TaskCard from './TaskCard';

interface TasksListProps {
  tasks: ActionItem[];
  pagination: Pagination;
  loading: boolean;
  loadingMore: boolean;
  isSearching: boolean;
  error: string | null;
  onQuickComplete: (taskId: string) => void;
  onEdit: (task: ActionItem) => void;
  onViewTranscript: (transcriptId: string) => void;
  onDelete: (taskId: string) => void;
  onLoadMore: () => void;
}

const TasksList: React.FC<TasksListProps> = ({
  tasks,
  pagination,
  loading,
  loadingMore,
  isSearching,
  error,
  onQuickComplete,
  onEdit,
  onViewTranscript,
  onDelete,
  onLoadMore
}) => {
  if (loading) {
    return (
      <div className="bg-gray-50 flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">
            Tasks ({tasks.length}{pagination.total > tasks.length ? ` of ${pagination.total}` : ''})
          </h3>
          {pagination.total > pagination.limit && (
            <div className="text-sm text-gray-500">
              Showing {Math.min(pagination.offset + 1, pagination.total)}-{Math.min(pagination.offset + tasks.length, pagination.total)} of {pagination.total}
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {tasks.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <div className="text-5xl mb-4">📋</div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">No tasks found</h3>
          <p className="text-sm">
            {isSearching 
              ? "No tasks match your current filters. Try adjusting your search criteria."
              : "You don't have any tasks yet. Create some transcripts to get started!"
            }
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-200">
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onQuickComplete={onQuickComplete}
              onEdit={onEdit}
              onViewTranscript={onViewTranscript}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
      
      {/* Load More Button */}
      {pagination.hasMore && (
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-center">
            <div className="text-sm text-gray-600 mb-3">
              Showing {tasks.length} of {pagination.total} tasks
            </div>
            <button
              onClick={onLoadMore}
              disabled={loadingMore}
              className="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loadingMore ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Loading more tasks...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Load {Math.min(pagination.limit, pagination.total - tasks.length)} More Tasks
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksList; 